/**
 * NEC1099CreatorModal — Form 1099-NEC (Nonemployee Compensation) creator.
 *
 * Pops up as a sibling option to the W-2 modal after a paystub is downloaded.
 * Numbers are ANNUALIZED January → December from the source paystub's
 * per-period amounts (same logic as the W-2 modal):
 *
 *     annual_value = current_period_value × pay_periods_per_year
 *
 * 1099-NEC boxes populated:
 *   • Box 1  Nonemployee compensation   = annualized gross
 *   • Box 4  Federal income tax withheld = annualized federal
 *   • Box 5  State tax withheld         = annualized state
 *   • Box 7  State income               = annualized gross
 *   • Box 6  State/Payer's state no.    = manual entry
 *
 * Year selector: current year back through 6 prior years (7 options).
 */
import React, { useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { X, FileText, Download, Loader2, Info } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const PERIODS_PER_YEAR = {
  weekly: 52,
  "bi-weekly": 26,
  biweekly: 26,
  "semi-monthly": 24,
  semimonthly: 24,
  monthly: 12,
};

const fmt = (n) => {
  const v = Number(n) || 0;
  return v.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const toNum = (v) => {
  const n = parseFloat(String(v || "").replace(/[^0-9.\-]/g, ""));
  return Number.isFinite(n) ? n : 0;
};

function NECRow({ label, k, hint, form, update }) {
  return (
    <div className="grid grid-cols-[1fr_140px] items-center gap-2">
      <label className="text-xs text-zinc-300">
        {label}
        {hint && <span className="text-[10px] text-zinc-500 ml-1">({hint})</span>}
      </label>
      <input
        type="text"
        inputMode="decimal"
        value={form[k]}
        onChange={(e) => update(k, e.target.value)}
        className="px-2 py-1 rounded bg-zinc-900 border border-zinc-700 text-white text-xs text-right focus:outline-none focus:ring-1 focus:ring-amber-500"
        data-testid={`nec-${k}`}
      />
    </div>
  );
}

export default function NEC1099CreatorModal({ open, onClose, formData, initialYear }) {
  const currentYear = new Date().getFullYear();
  const yearOptions = useMemo(
    () => Array.from({ length: 7 }, (_, i) => currentYear - i),
    [currentYear]
  );
  const defaultYear = initialYear || (currentYear - 1);

  const sourcePaystub = useMemo(() => {
    const list = formData?.paystubs || [];
    return list[list.length - 1] || null;
  }, [formData]);

  const payFrequency = String(formData?.payFrequency || "bi-weekly").toLowerCase();
  const periodsPerYear = PERIODS_PER_YEAR[payFrequency] || 26;

  // Wizard stubs nest under `current.*`; uploaded ones may be flat.
  const cur = sourcePaystub?.current || sourcePaystub || {};
  const curGross = toNum(cur.grossPay);
  const curFederal = toNum(cur.federalTax ?? cur.federal);
  const curState = toNum(cur.stateTax ?? cur.state);

  const annualGross = +(curGross * periodsPerYear).toFixed(2);
  const annualFederal = +(curFederal * periodsPerYear).toFixed(2);
  const annualState = +(curState * periodsPerYear).toFixed(2);

  const [taxYear, setTaxYear] = useState(defaultYear);
  // 2026-06-24 — Sync when caller updates initialYear (year picker on prompt)
  React.useEffect(() => {
    if (initialYear) setTaxYear(initialYear);
  }, [initialYear, open]);
  const [form, setForm] = useState({
    // Payer
    payer_tin: "",                  // EIN — manual entry
    payer_phone: "",                // manual
    // Recipient
    recipient_tin: formData?.employeeSSN ? `XXX-XX-${String(formData.employeeSSN).slice(-4)}` : "",
    account_number: "",
    // Boxes — auto-filled from annualization
    box1_nonemployee_comp: annualGross,
    box4_federal_tax: annualFederal,
    box5_state_tax: annualState,
    box6_state_payer_id: "",
    box7_state_income: annualGross,
    box2_direct_sales: false,
  });

  const [generating, setGenerating] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);

  if (!open) return null;

  const update = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const buildPayload = () => ({
    tax_year: taxYear,
    // Payer = the company on the paystub
    payer_name: formData.companyName || "",
    payer_address1: formData.companyAddress1 || "",
    payer_address2: formData.companyAddress2 || null,
    payer_city: formData.companyCity || "",
    payer_state: formData.companyState || "",
    payer_zip: formData.companyZip || "",
    payer_phone: form.payer_phone || "",
    payer_tin: form.payer_tin || "",
    // Recipient = the worker on the paystub
    recipient_name: formData.employeeName || "",
    recipient_address1: formData.employeeAddress1 || "",
    recipient_address2: formData.employeeAddress2 || null,
    recipient_city: formData.employeeCity || "",
    recipient_state: formData.employeeState || formData.companyState || "",
    recipient_zip: formData.employeeZip || "",
    recipient_tin: form.recipient_tin || "",
    account_number: form.account_number || "",
    box2_direct_sales: !!form.box2_direct_sales,
    box1_nonemployee_comp: toNum(form.box1_nonemployee_comp),
    box4_federal_tax: toNum(form.box4_federal_tax),
    box5_state_tax: toNum(form.box5_state_tax),
    box6_state_payer_id: form.box6_state_payer_id || "",
    box7_state_income: toNum(form.box7_state_income),
  });

  const previewNEC = async () => {
    if (!form.payer_tin) { toast.error("Enter Payer's TIN (EIN)"); return; }
    if (!form.recipient_tin) { toast.error("Enter Recipient's TIN (SSN)"); return; }
    setPreviewing(true);
    try {
      const res = await axios.post(`${API}/api/nec/generate-pdf`, buildPayload(), { responseType: "blob" });
      if (previewUrl) window.URL.revokeObjectURL(previewUrl);
      const url = window.URL.createObjectURL(res.data);
      setPreviewUrl(url);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to preview 1099-NEC");
    } finally {
      setPreviewing(false);
    }
  };

  const generate = async () => {
    if (!form.payer_tin) { toast.error("Enter Payer's TIN (EIN)"); return; }
    if (!form.recipient_tin) { toast.error("Enter Recipient's TIN (SSN)"); return; }
    setGenerating(true);
    try {
      const res = await axios.post(`${API}/api/nec/generate-pdf`, buildPayload(), { responseType: "blob" });
      const url = window.URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      const safeName = (formData.employeeName || "Recipient").replace(/[^a-z0-9_-]+/gi, "_");
      a.download = `1099-NEC_${taxYear}_${safeName}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success(`1099-NEC ${taxYear} downloaded`);
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to generate 1099-NEC");
    } finally {
      setGenerating(false);
    }
  };

  const freqLabel = ({
    weekly: "weekly (52 pay periods)",
    "bi-weekly": "bi-weekly (26 pay periods)",
    biweekly: "bi-weekly (26 pay periods)",
    "semi-monthly": "semi-monthly (24 pay periods)",
    semimonthly: "semi-monthly (24 pay periods)",
    monthly: "monthly (12 pay periods)",
  })[payFrequency] || `${periodsPerYear} pay periods`;

  return (
    <div className="fixed inset-0 z-[70] bg-black/75 backdrop-blur-sm flex items-center justify-center p-4" data-testid="nec-modal">
      <div className="bg-zinc-950 border border-zinc-700 rounded-2xl w-full max-w-3xl max-h-[92vh] overflow-y-auto shadow-2xl">
        <div className="sticky top-0 z-10 bg-zinc-950 border-b border-zinc-800 px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-amber-500/15 border border-amber-500/40">
              <FileText className="w-5 h-5 text-amber-400" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-white">Form 1099-NEC · Nonemployee Compensation</h2>
              <p className="text-[11px] text-zinc-500">Annualized January → December from this paystub&apos;s per-period amounts</p>
            </div>
          </div>
          <button onClick={onClose} className="text-zinc-400 hover:text-white p-1 rounded-lg hover:bg-zinc-800" data-testid="nec-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 space-y-5">
          {/* Tax year + Payer TIN + Recipient TIN */}
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="block text-[10px] uppercase tracking-wider text-zinc-400 mb-1">Tax year</label>
              <select
                value={taxYear}
                onChange={(e) => setTaxYear(Number(e.target.value))}
                className="w-full px-3 py-2 rounded-lg bg-zinc-900 border border-zinc-700 text-white text-sm focus:outline-none focus:ring-1 focus:ring-amber-500"
                data-testid="nec-year"
              >
                {yearOptions.map((y) => (
                  <option key={y} value={y}>{y}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-[10px] uppercase tracking-wider text-zinc-400 mb-1">
                Payer&apos;s TIN (EIN) <span className="text-amber-400">*</span>
              </label>
              <input
                type="text"
                value={form.payer_tin}
                onChange={(e) => update("payer_tin", e.target.value)}
                placeholder="46-4584485"
                className="w-full px-3 py-2 rounded-lg bg-zinc-900 border border-zinc-700 text-white text-sm focus:outline-none focus:ring-1 focus:ring-amber-500"
                data-testid="nec-payer-tin"
              />
            </div>
            <div>
              <label className="block text-[10px] uppercase tracking-wider text-zinc-400 mb-1">
                Recipient&apos;s TIN (SSN) <span className="text-amber-400">*</span>
              </label>
              <input
                type="text"
                value={form.recipient_tin}
                onChange={(e) => update("recipient_tin", e.target.value)}
                placeholder="XXX-XX-1234"
                className="w-full px-3 py-2 rounded-lg bg-zinc-900 border border-zinc-700 text-white text-sm focus:outline-none focus:ring-1 focus:ring-amber-500"
                data-testid="nec-recipient-tin"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-[10px] uppercase tracking-wider text-zinc-400 mb-1">Payer phone (optional)</label>
              <input
                type="text"
                value={form.payer_phone}
                onChange={(e) => update("payer_phone", e.target.value)}
                placeholder="(555) 123-4567"
                className="w-full px-3 py-2 rounded-lg bg-zinc-900 border border-zinc-700 text-white text-sm focus:outline-none focus:ring-1 focus:ring-amber-500"
                data-testid="nec-payer-phone"
              />
            </div>
            <div>
              <label className="block text-[10px] uppercase tracking-wider text-zinc-400 mb-1">Account number (optional)</label>
              <input
                type="text"
                value={form.account_number}
                onChange={(e) => update("account_number", e.target.value)}
                placeholder="ACCT-001"
                className="w-full px-3 py-2 rounded-lg bg-zinc-900 border border-zinc-700 text-white text-sm focus:outline-none focus:ring-1 focus:ring-amber-500"
                data-testid="nec-account-no"
              />
            </div>
          </div>

          {/* Auto-calc box values (editable) */}
          <div className="rounded-xl border border-zinc-800 bg-zinc-900/40 p-4">
            <div className="flex items-start gap-2 mb-3">
              <Info className="w-4 h-4 text-amber-400 flex-shrink-0 mt-0.5" />
              <p className="text-xs text-zinc-400">
                These numbers are projected <span className="text-amber-300 font-semibold">January through December {taxYear}</span> based
                on the paystub&apos;s per-period amounts paid <span className="text-amber-300 font-semibold">{freqLabel}</span>.
                Per-period gross <span className="text-amber-300 font-mono">${fmt(curGross)}</span> × <span className="text-amber-300 font-mono">{periodsPerYear}</span> = annual <span className="text-amber-300 font-mono">${fmt(annualGross)}</span>.
                Override any value before generating.
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2">
              <NECRow form={form} update={update} label="Box 1 — Nonemployee compensation" k="box1_nonemployee_comp" hint="Jan-Dec annual gross" />
              <NECRow form={form} update={update} label="Box 4 — Federal income tax withheld" k="box4_federal_tax" hint="Jan-Dec federal" />
              <NECRow form={form} update={update} label="Box 5 — State tax withheld" k="box5_state_tax" hint="Jan-Dec state" />
              <div className="grid grid-cols-[1fr_140px] items-center gap-2">
                <label className="text-xs text-zinc-300">
                  Box 6 — State / Payer&apos;s state no.
                </label>
                <input
                  type="text"
                  value={form.box6_state_payer_id}
                  onChange={(e) => update("box6_state_payer_id", e.target.value)}
                  placeholder="023-5689-1"
                  className="px-2 py-1 rounded bg-zinc-900 border border-zinc-700 text-white text-xs text-right focus:outline-none focus:ring-1 focus:ring-amber-500"
                  data-testid="nec-box6_state_payer_id"
                />
              </div>
              <NECRow form={form} update={update} label="Box 7 — State income" k="box7_state_income" hint="Jan-Dec gross" />
            </div>
            <label className="mt-3 flex items-center gap-2 text-xs text-zinc-400">
              <input
                type="checkbox"
                checked={form.box2_direct_sales}
                onChange={(e) => update("box2_direct_sales", e.target.checked)}
                className="rounded border-zinc-700"
                data-testid="nec-box2-checkbox"
              />
              Box 2 — Payer made direct sales totaling $5,000+ to recipient for resale
            </label>
          </div>

          {/* Inline PDF preview pane */}
          {previewUrl && (
            <div className="rounded-xl border border-amber-500/40 bg-zinc-900/60 p-2">
              <div className="flex items-center justify-between mb-2 px-1">
                <p className="text-xs font-bold text-amber-300 uppercase tracking-wider">Preview · click Preview again after edits</p>
                <button
                  onClick={() => { if (previewUrl) window.URL.revokeObjectURL(previewUrl); setPreviewUrl(null); }}
                  className="text-zinc-400 hover:text-white text-xs"
                  data-testid="nec-close-preview"
                >
                  Close preview
                </button>
              </div>
              <iframe
                src={previewUrl}
                title="1099-NEC preview"
                className="w-full h-[520px] bg-white rounded border border-zinc-700"
                data-testid="nec-preview-frame"
              />
            </div>
          )}

          <div className="flex items-center justify-end gap-2 pt-2">
            <button
              onClick={onClose}
              className="px-4 py-2 rounded-lg bg-zinc-900 border border-zinc-700 hover:bg-zinc-800 text-zinc-200 text-sm font-medium"
            >
              Cancel
            </button>
            <button
              onClick={previewNEC}
              disabled={previewing || generating}
              className="px-4 py-2 rounded-lg bg-zinc-800 border border-amber-500/40 hover:bg-zinc-700 disabled:opacity-60 text-amber-300 text-sm font-semibold flex items-center gap-2"
              data-testid="nec-preview"
            >
              {previewing ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileText className="w-4 h-4" />}
              Preview 1099-NEC
            </button>
            <button
              onClick={generate}
              disabled={generating || previewing}
              className="px-5 py-2 rounded-lg bg-amber-600 hover:bg-amber-500 disabled:opacity-60 text-white text-sm font-semibold flex items-center gap-2"
              data-testid="nec-generate"
            >
              {generating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
              Generate &amp; Download 1099-NEC ({taxYear})
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
